import React from 'react';
import Icon from '../../../components/AppIcon';

const OverallProgressDashboard = ({ 
  overallProgress, 
  estimatedTimeRemaining, 
  currentStage, 
  totalStages 
}) => {
  const formatTimeRemaining = (minutes) => {
    if (minutes < 1) return 'Less than 1 minute';
    if (minutes < 60) return `${Math.round(minutes)} minutes`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    return `${hours}h ${remainingMinutes}m`;
  };

  const getProgressColor = () => {
    if (overallProgress >= 90) return 'bg-success';
    if (overallProgress >= 70) return 'bg-primary';
    if (overallProgress >= 40) return 'bg-warning';
    return 'bg-error';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-foreground">Processing Progress</h2>
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={20} className="text-primary" />
          <span className="text-sm text-muted-foreground">
            Stage {currentStage} of {totalStages}
          </span>
        </div>
      </div>
      {/* Main Progress Circle */}
      <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
        <div className="relative flex items-center justify-center">
          <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
            {/* Background circle */}
            <circle
              cx="60"
              cy="60"
              r="50"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-muted"
            />
            {/* Progress circle */}
            <circle
              cx="60"
              cy="60"
              r="50"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 50}`}
              strokeDashoffset={`${2 * Math.PI * 50 * (1 - overallProgress / 100)}`}
              className={`transition-all duration-500 ease-out ${
                overallProgress >= 90 ? 'text-success' :
                overallProgress >= 70 ? 'text-primary' :
                overallProgress >= 40 ? 'text-warning' : 'text-error'
              }`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">
                {Math.round(overallProgress)}%
              </div>
              <div className="text-xs text-muted-foreground">Complete</div>
            </div>
          </div>
        </div>

        {/* Progress Details */}
        <div className="flex-1 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Overall Progress</span>
                <span className="text-sm font-medium text-foreground">
                  {Math.round(overallProgress)}%
                </span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ease-out ${getProgressColor()}`}
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Time Remaining</span>
                <span className="text-sm font-medium text-foreground">
                  {formatTimeRemaining(estimatedTimeRemaining)}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  Started at {new Date()?.toLocaleTimeString('en-US', {
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Stage Progress Indicators */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground">Processing Stages</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { name: 'Normalize', icon: 'Filter', stage: 1 },
                { name: 'EAN Match', icon: 'Hash', stage: 2 },
                { name: 'MPN Match', icon: 'Tag', stage: 3 },
                { name: 'AI Match', icon: 'Brain', stage: 4 }
              ]?.map((item) => (
                <div key={item?.stage} className={`
                  flex items-center space-x-2 p-2 rounded-md text-xs
                  ${currentStage > item?.stage ? 'bg-success bg-opacity-10 text-success' :
                    currentStage === item?.stage ? 'bg-primary bg-opacity-10 text-primary': 'bg-muted text-muted-foreground'}
                `}>
                  <Icon 
                    name={currentStage > item?.stage ? 'Check' : item?.icon} 
                    size={12} 
                  />
                  <span className="font-medium">{item?.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
            <div className="text-center">
              <div className="text-lg font-bold text-foreground">2,847</div>
              <div className="text-xs text-muted-foreground">Products</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-foreground">1,923</div>
              <div className="text-xs text-muted-foreground">Processed</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-foreground">924</div>
              <div className="text-xs text-muted-foreground">Remaining</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverallProgressDashboard;